import * as React from "react";
import { Box, Button, IconButton, Divider, Paper } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { ActiveRallyVideo } from "./ActiveRallyVideo";
import { FirefightClip } from "./GameStory_firefights";
import { KitchenArrivalRow } from "./RallyBoardPopup";
import { AiTurn } from "./ActiveRallyVideo"; //RallyColumnAskAIPanel";
import {
  PlayerIndexMap,
  RenderPlayerIdentity,
} from "./GameStoryDialog_UPDATED";

type AiTurnStatus = "pending" | "done" | "error";

type ClipFocus = {
  rallyIndex: number;
  videoUrl: string;
  segmentStartMs: number;
  segmentEndMs: number;
  rangeStartMs: number;
  rangeEndMs: number;
  rangeMode: "loop" | "full";
  kind: string;
};

type AiTurnGlobal = {
  id: string;
  createdAtMs: number;
  question: string;
  status: AiTurnStatus;
  responseText?: string;
  responseJson?: any;
  error?: string;
  focus: ClipFocus;
  contextLabel: string;

  packageId: string;
  rallyIndex: number;
};

export function perRallyTurnsKey(packageId: string, rallyIndex: number) {
  return `poc2.aiTurns.v1.${packageId}.rally.${rallyIndex}`;
}

export function readPerRallyTurns(
  packageId: string,
  rallyIndex: number,
): AiTurn[] {
  try {
    const raw = localStorage.getItem(perRallyTurnsKey(packageId, rallyIndex));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as AiTurn[]) : [];
  } catch {
    return [];
  }
}

// Prefer the per-rally turn (what ActiveRallyVideo is showing) if it exists
function pickBestTurnForDisplay(
  t: AiTurnGlobal,
  perRallyByRallyIndex: Map<number, AiTurn[]>,
): AiTurnGlobal | AiTurn {
  const arr = perRallyByRallyIndex.get(t.rallyIndex);
  const found = arr?.find((x) => x?.id === t.id);
  return found ?? t;
}

function globalTurnsStorageKey() {
  return `poc2.aiTurnsGlobal.v1`;
}

// function perRallyTurnsKey(packageId: string, rallyIndex: number) {
//   return `poc2.aiTurns.v1.${packageId}.rally.${rallyIndex}`;
// }

export function deletePerRallyTurn(
  packageId: string,
  rallyIndex: number,
  turnId: string,
) {
  try {
    const key = perRallyTurnsKey(packageId, rallyIndex);
    const raw = localStorage.getItem(key);
    if (!raw) return;

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return;

    // const next = parsed.filter((t: any) => t?.id !== turnId);
    // localStorage.setItem(key, JSON.stringify(next));

    const next = parsed.filter((t: any) => t?.id !== turnId);
    localStorage.setItem(key, JSON.stringify(next));

    window.dispatchEvent(
      new CustomEvent("poc2-ai-turns-updated", {
        detail: {
          packageId,
          rallyIndex,
          scope: "per-rally",
        },
      }),
    );
  } catch {}
}

export function readGlobalTurns(): AiTurnGlobal[] {
  try {
    const raw = localStorage.getItem(globalTurnsStorageKey());
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as AiTurnGlobal[]) : [];
  } catch {
    return [];
  }
}

// export function writeGlobalTurns(next: AiTurnGlobal[]) {
//   try {
//     localStorage.setItem(globalTurnsStorageKey(), JSON.stringify(next));
//   } catch {}
// }

export function writeGlobalTurns(next: AiTurnGlobal[]) {
  try {
    localStorage.setItem(globalTurnsStorageKey(), JSON.stringify(next));

    window.dispatchEvent(new CustomEvent("poc2-ai-turns-updated"));
  } catch {}
}

export function fmtTime(ms: number) {
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return "";
  }
}

export function writePerRallyTurns(
  packageId: string,
  rallyIndex: number,
  turns: AiTurn[],
) {
  try {
    localStorage.setItem(
      perRallyTurnsKey(packageId, rallyIndex),
      JSON.stringify(turns),
    );

    window.dispatchEvent(
      new CustomEvent("poc2-ai-turns-updated", {
        detail: {
          packageId,
          rallyIndex,
          scope: "per-rally",
        },
      }),
    );
  } catch {
    // ignore
  }
}

export function clipStartSec(t: AiTurnGlobal) {
  const rel = (t.focus.rangeStartMs - t.focus.segmentStartMs) / 1000;
  return Math.max(0, rel);
}

export function clipEndSec(t: AiTurnGlobal) {
  const rel = (t.focus.rangeEndMs - t.focus.segmentStartMs) / 1000;
  return Math.max(0, rel);
}

export function truncateText(s: string, maxChars: number) {
  if (!s) return "";
  if (s.length <= maxChars) return s;
  return s.slice(0, maxChars).trimEnd() + "…";
}

export function buildFullAnswerText(t: any): string {

  if (t?.status === "error") {
    return t?.error ?? t?.responseText ?? "Error";
  }
  
  const j = t?.responseJson ?? {};

  // Prefer structured fields when present (this matches your screenshot)
  const answer = (
    j.answerToQuestion ??
    j.answer ??
    j.Answer ??
    t?.responseText ??
    ""
  ).trim();

  const rallyOverview = j.rallyOverview ?? {};
  const turning =
    (
      rallyOverview.keyTurningPoint ??
      rallyOverview.turningPoint ??
      j.keyTurningPoint ??
      j.turningPoint ??
      ""
    ).trim?.() ?? "";

  const eventSeqRaw =
    rallyOverview.eventSequence ?? j.eventSequence ?? j.bullets ?? [];

  const eventSeq: string[] = Array.isArray(eventSeqRaw)
    ? eventSeqRaw
        .map((x: any) => {
          if (!x) return "";
          if (typeof x === "string") return x;
          // if they ever become objects
          return x.text ?? x.value ?? x.point ?? "";
        })
        .map((s) => String(s).trim())
        .filter(Boolean)
    : [];

  const winner = (j.winnerSide ?? j.winner ?? "").toString().trim();

  const confRaw = j.overallConfidence ?? j.confidence;
  const confidence =
    typeof confRaw === "number"
      ? `${Math.round(confRaw * 100)}%`
      : confRaw
        ? String(confRaw).trim()
        : "";

  let out = "";

  // The right side shows "Answer:" label
  if (answer) out += `Answer: ${answer}\n`;

  // Then "Turning point:"
  if (turning) out += `\nTurning point: ${turning}\n`;

  // Then bullets
  if (eventSeq.length > 0) {
    out += `\n`;
    for (const b of eventSeq) out += `• ${b}\n`;
  }

  // Then winner/confidence line
  if (winner || confidence) {
    out += `\nWinner: ${winner || "?"}`;
    if (confidence) out += `  •  Confidence: ${confidence}`;
    out += `\n`;
  }

  return out.trim();
}

function buildFullAnswerText_oldold(t: AiTurnGlobal): string {
  const rawText = (t?.responseText ?? "").trim();
  const j: any = t?.responseJson;

  // If responseText already contains richer formatting, use it as source of truth.
  // (This matches what your right-side AI panel is showing.)
  const looksRich =
    rawText.includes("Turning point:") ||
    rawText.includes("Turning point") ||
    rawText.includes("Winner:") ||
    rawText.includes("Confidence:") ||
    rawText.includes("• ");

  if (looksRich) return rawText;

  // Otherwise, try to build from JSON if present
  if (!j) return rawText;

  const answer = (
    j.answer ??
    j.Answer ??
    j.summary ??
    j.response ??
    rawText ??
    ""
  ).trim();

  const turning =
    (
      j.turning_point ??
      j.turningPoint ??
      j.turning_point_text ??
      j.turningPointText ??
      j.turning ??
      j.turningPointSummary ??
      ""
    )?.trim?.() ?? "";

  // bullets can be strings OR objects; support common shapes
  const bulletsRaw =
    j.bullets ??
    j.key_points ??
    j.keyPoints ??
    j.points ??
    j.breakdown ??
    j.key_takeaways ??
    j.keyTakeaways ??
    [];

  const bullets: string[] = Array.isArray(bulletsRaw)
    ? bulletsRaw
        .map((b: any) => {
          if (!b) return "";
          if (typeof b === "string") return b;
          // common object shapes
          return (
            b.text ?? b.value ?? b.point ?? b.bullet ?? b.description ?? ""
          );
        })
        .map((s) => String(s).trim())
        .filter(Boolean)
    : [];

  const winner =
    (
      j.winner ??
      j.Winner ??
      j.winnerTeam ??
      (j.winner_team != null ? `Team ${j.winner_team}` : "")
    )?.trim?.() ?? "";

  const confidenceRaw = j.confidence ?? j.Confidence;
  const confidence =
    typeof confidenceRaw === "number"
      ? `${Math.round(confidenceRaw * 100)}%`
      : String(confidenceRaw ?? "").trim() || "";

  let out = "";
  if (answer) out += `Answer: ${answer}\n`;

  if (turning) out += `\nTurning point: ${turning}\n`;

  if (bullets.length > 0) {
    out += `\n`;
    for (const b of bullets) out += `• ${b}\n`;
  }

  if (winner || confidence) {
    out += `\nWinner: ${winner || "?"}`;
    if (confidence) out += `  •  Confidence: ${confidence}`;
    out += `\n`;
  }

  const built = out.trim();

  // If responseText exists but wasn't detected as "rich", still pick the longer one.
  // (Sometimes the labels differ slightly.)
  if (rawText && rawText.length > built.length) return rawText;

  return built;
}
function buildFullAnswerText_OLD(t: AiTurnGlobal): string {
  // Prefer responseJson if present, because it contains structured fields
  const j = t?.responseJson;
  if (!j) return t?.responseText ?? "";

  // Your extracted JSON often looks like:
  // { answer: "...", turning_point: "...", bullets: [...], winner: "...", confidence: 0.86 }
  // but names may vary; handle common variants safely.

  const answer =
    j.answer ?? j.Answer ?? j.summary ?? j.response ?? t?.responseText ?? "";

  const turning =
    j.turning_point ??
    j.turningPoint ??
    j.turning_point_text ??
    j.turningPointText ??
    "";

  const bullets: string[] =
    j.bullets ?? j.key_points ?? j.keyPoints ?? j.points ?? j.breakdown ?? [];

  const winner =
    j.winner ??
    j.Winner ??
    (j.winner_team != null ? `Team ${j.winner_team}` : "");

  const confidenceRaw = j.confidence ?? j.Confidence;
  const confidence =
    typeof confidenceRaw === "number"
      ? `${Math.round(confidenceRaw * 100)}%`
      : "";

  let out = "";
  if (answer) out += `Answer: ${answer}\n`;

  if (turning) out += `\nTurning point: ${turning}\n`;

  if (Array.isArray(bullets) && bullets.length > 0) {
    out += `\n`;
    for (const b of bullets) {
      if (!b) continue;
      out += `• ${String(b)}\n`;
    }
  }

  if (winner || confidence) {
    out += `\nWinner: ${winner || "?"}`;
    if (confidence) out += `  •  Confidence: ${confidence}`;
    out += `\n`;
  }

  return out.trim();
}
export function AiTurnsHistoryPopup(props: {
  insightsToCvPlayerIndex: PlayerIndexMap;
  open: boolean;
  onClose: () => void;

  // current package (we show/play turns for this package only)
  packageId: string;
  rows: KitchenArrivalRow[];
  renderPlayerIdentity: RenderPlayerIdentity;

  // ActiveRallyVideo deps
  firefightsClips: FirefightClip[];
  firefightRallySet: Set<number>;

  onOpenRally?: (p: { packageId: string; rallyIndex: number }) => void;
}) {
  const {
    open,
    onClose,
    onOpenRally,
    packageId,
    rows,
    firefightsClips,
    firefightRallySet,
    insightsToCvPlayerIndex,
    renderPlayerIdentity,
  } = props;

  const [expandedTurnIds, setExpandedTurnIds] = React.useState<
    Record<string, boolean>
  >({});

  const [turns, setTurns] = React.useState<AiTurnGlobal[]>([]);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  const [previewNonce, setPreviewNonce] = React.useState(0);

  // movable panel (matches your fastReactions pattern)
  const [pos, setPos] = React.useState(() => ({
    x: 8, //Math.max(18, Math.round(window.innerWidth * 0.03)),
    y: 50,
  }));

  const dragRef = React.useRef<{
    startX: number;
    startY: number;
    origX: number;
    origY: number;
    dragging: boolean;
  } | null>(null);

  const onMouseDownHeader = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origX: pos.x,
        origY: pos.y,
        dragging: true,
      };

      const onMove = (ev: MouseEvent) => {
        const d = dragRef.current;
        if (!d || !d.dragging) return;

        const dx = ev.clientX - d.startX;
        const dy = ev.clientY - d.startY;

        const nextX = Math.max(
          8,
          Math.min(window.innerWidth - 220, d.origX + dx),
        );
        const nextY = Math.max(
          8,
          Math.min(window.innerHeight - 140, d.origY + dy),
        );

        setPos({ x: nextX, y: nextY });
      };

      const onUp = () => {
        if (dragRef.current) dragRef.current.dragging = false;
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };

      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [pos.x, pos.y],
  );

  React.useEffect(() => {
    if (!open) return;

    const reload = () => {
      // const t = readGlobalTurns()
      //   .filter(
      //     (x) =>
      //       x &&
      //       x.packageId === packageId &&
      //       typeof x.rallyIndex === "number" &&
      //       x.focus &&
      //       typeof x.focus.videoUrl === "string" &&
      //       typeof x.focus.segmentStartMs === "number" &&
      //       typeof x.focus.segmentEndMs === "number" &&
      //       typeof x.focus.rangeStartMs === "number" &&
      //       typeof x.focus.rangeEndMs === "number",
      //   )
      //   .sort((a, b) => (b.createdAtMs ?? 0) - (a.createdAtMs ?? 0));

      const global = readGlobalTurns().filter(
        (x) =>
          x &&
          x.packageId === packageId &&
          typeof x.rallyIndex === "number" &&
          x.focus &&
          typeof x.focus.videoUrl === "string" &&
          typeof x.focus.segmentStartMs === "number" &&
          typeof x.focus.segmentEndMs === "number" &&
          typeof x.focus.rangeStartMs === "number" &&
          typeof x.focus.rangeEndMs === "number",
      );

      const t = global
        .filter((x) => {
          const perRally = readPerRallyTurns(packageId, x.rallyIndex);
          return perRally.some((p) => p?.id === x.id);
        })
        .sort((a, b) => (b.createdAtMs ?? 0) - (a.createdAtMs ?? 0));

      setTurns(t);

      setSelectedId((prev) => {
        if (prev && t.some((x) => x.id === prev)) return prev;
        return t[0]?.id ?? null;
      });
    };

    reload();

    const storageHandler = (e: StorageEvent) => {
      if (e.key === globalTurnsStorageKey()) reload();
    };

    const customHandler = () => reload();

    window.addEventListener("storage", storageHandler);
    window.addEventListener("poc2-ai-turns-updated", customHandler);

    return () => {
      window.removeEventListener("storage", storageHandler);
      window.removeEventListener("poc2-ai-turns-updated", customHandler);
    };
  }, [open, packageId]);

  const perRallyByRallyIndex = React.useMemo(() => {
    const rallyIndexes = Array.from(new Set(turns.map((t) => t.rallyIndex)));
    const m = new Map<number, AiTurn[]>();
    for (const ri of rallyIndexes) {
      m.set(ri, readPerRallyTurns(packageId, ri));
    }
    return m;
  }, [packageId, turns]);

  const selected = React.useMemo(() => {
    if (!selectedId) {
// console.error("selectedId not there");
      return null;
    }

    const aiTurn = turns.find((t) => t.id === selectedId) ?? null;

    // console.error("aiTurn", {aiTurn});

    return aiTurn;
  }, [turns, selectedId]);

  // Group by rallyIndex (primary sort unit)
  const groups = React.useMemo(() => {
    const map = new Map<number, AiTurnGlobal[]>();

    for (const t of turns) {
      const arr = map.get(t.rallyIndex) ?? [];
      arr.push(t);
      map.set(t.rallyIndex, arr);
    }

    const out = Array.from(map.entries()).map(([rallyIndex, arr]) => {
      arr.sort((a, b) => (b.createdAtMs ?? 0) - (a.createdAtMs ?? 0));
      const latestMs = Math.max(...arr.map((x) => x.createdAtMs ?? 0));
      return { rallyIndex, latestMs, turns: arr };
    });

    // main sort over rallies: newest activity first
    out.sort((a, b) => {
      if (b.latestMs !== a.latestMs) return b.latestMs - a.latestMs;
      return b.rallyIndex - a.rallyIndex;
    });

    return out;
  }, [turns]);

  const selectedRow = React.useMemo(() => {
    if (!selected) return null;
    return rows[selected.rallyIndex] ?? null;
  }, [rows, selected?.id]);

  const clipRow = React.useMemo(() => {
    // console.error("selected useMemo", {selected});

    if (!selected || !selectedRow) return null;

    if (!selected.focus) return null;

    // Drive the preview with the turn’s focused clip.
    // The focus already stores segmentStart/End, so ActiveRallyVideo timelines/overlays align.
    return {
      ...selectedRow,
      videoUrl: selected.focus.videoUrl || selectedRow.videoUrl,
      segmentStartMs: selected.focus.segmentStartMs,
      segmentEndMs: selected.focus.segmentEndMs,
    } as KitchenArrivalRow;
  }, [selected?.id, selectedRow]);

  // const deleteTurnById = React.useCallback(
  //   (turnId: string) => {
  //     setTurns((cur) => {
  //       const next = cur.filter((t) => t.id !== turnId);

  //       // rewrite global storage (keep other packages)
  //       const others = readGlobalTurns().filter((t) => t.packageId !== packageId);
  //       writeGlobalTurns(
  //         [...others, ...next].sort((a, b) => (b.createdAtMs ?? 0) - (a.createdAtMs ?? 0)),
  //       );

  //       // selection: if we deleted the selected one, select next[0] (or null)
  //       setSelectedId((prevSel) => {
  //         if (prevSel !== turnId) return prevSel;
  //         return next[0]?.id ?? null;
  //       });

  //       return next;
  //     });
  //   },
  //   [packageId],
  // );

  const deleteTurnById = React.useCallback(
    (turnId: string) => {
      setTurns((cur) => {
        const doomed = cur.find((t) => t.id === turnId) ?? null;
        const next = cur.filter((t) => t.id !== turnId);

        // update global storage (keep other packages)
        const others = readGlobalTurns().filter(
          (t) => t.packageId !== packageId,
        );
        writeGlobalTurns(
          [...others, ...next].sort(
            (a, b) => (b.createdAtMs ?? 0) - (a.createdAtMs ?? 0),
          ),
        );

        // ALSO delete from ActiveRallyVideo's per-rally storage
        if (doomed) {
          deletePerRallyTurn(packageId, doomed.rallyIndex, turnId);
        }

        // keep selection sane
        setSelectedId((prevSel) => {
          if (prevSel !== turnId) return prevSel;
          return next[0]?.id ?? null;
        });

        // force the preview ActiveRallyVideo to remount & re-read storage
        setPreviewNonce((n) => n + 1);

        return next;
      });
    },
    [packageId],
  );

  const deleteSelected = React.useCallback(() => {
    if (!selected) return;
    deleteTurnById(selected.id);
  }, [selected?.id, deleteTurnById]);

  if (!open) return null;

  return (
    <Paper
      data-happy-path="ai-history-popup"
      elevation={10}
      sx={{
        position: "fixed",
        left: pos.x,
        top: pos.y,
        width: "calc(100vw - 36px)",
        maxWidth: "calc(100vw - 36px)",
        height: "92vh",
        maxHeight: "calc(100vh - 120px)",
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        zIndex: 2200,
        border: "1px solid rgba(0,0,0,0.14)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(0,0,0,0.12)",
          cursor: "grab",
          "&:active": { cursor: "grabbing" },
        }}
        onMouseDown={onMouseDownHeader}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
        >
          <Box sx={{ fontWeight: 900, whiteSpace: "nowrap" }}>AI History</Box>
          <Box sx={{ opacity: 0.75, fontSize: 12, whiteSpace: "nowrap" }}>
            {turns.length} turn{turns.length !== 1 ? "s" : ""} • {groups.length}{" "}
            rall{groups.length === 1 ? "y" : "ies"}
          </Box>
        </Box>

        <Button
          size="small"
          variant="text"
          onClick={onClose}
          startIcon={<CloseIcon />}
          sx={{ textTransform: "none", fontWeight: 900, whiteSpace: "nowrap" }}
        >
          Close
        </Button>
      </Box>

      <Divider />

      {/* Body */}
      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        {/* Left: rally groups */}
        <Box
          sx={{
            width: "25%",
            minWidth: 220,
            maxWidth: 460,
            borderRight: "1px solid rgba(0,0,0,0.12)",
            overflow: "auto",
          }}
        >
          {groups.map((g) => (
            <Box key={`r${g.rallyIndex}`} sx={{ p: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                  mb: 0.25,
                }}
              >
                <Box sx={{ fontWeight: 900 }}>Rally {g.rallyIndex + 1}</Box>
                <Box sx={{ opacity: 0.7, fontSize: 12 }}>
                  {fmtTime(g.latestMs)}
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.7 }}>
                {g.turns.map((t) => {
                  if (!t.focus) {
                    // console.error({t});
                    return null;
                  }

                  const isSel = t.id === selectedId;

                  return (
                    <Box
                      key={t.id}
                      sx={{
                        p: 0.9,
                        borderRadius: 1.5,
                        border: isSel
                          ? "2px solid rgba(0,0,0,0.55)"
                          : "1px solid rgba(0,0,0,0.18)",
                        bgcolor: isSel
                          ? "rgba(0,0,0,0.06)"
                          : "rgba(0,0,0,0.03)",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <Box
                          onClick={() => setSelectedId(t.id)}
                          sx={{ cursor: "pointer", flex: 1 }}
                        >
                          <Box sx={{ fontSize: 12, opacity: 0.75 }}>
                            {t.contextLabel ?? "—"}
                            {t.focus.rangeMode === "loop" ? " • loop" : ""}
                            {t.status === "error" ? " • error" : ""}
                            {t.status === "pending" ? " • thinking" : ""}
                          </Box>

                          <Box sx={{ mt: 0.4, fontWeight: 900 }}>
                            {t.question}
                          </Box>

                          {t.status === "error" && (
                            <Box
                              sx={{
                                mt: 0.6,
                                whiteSpace: "pre-wrap",
                                color: "#b00020",
                              }}
                            >
                              {t.error ?? t.responseText ?? "Error"}
                            </Box>
                          )}

                          {t.status === "done" &&
                            (t.responseText || t.responseJson) &&
                            (() => {
                              const tBest = pickBestTurnForDisplay(
                                t,
                                perRallyByRallyIndex,
                              );

                              // console.error({tBest});
                              const full = buildFullAnswerText(tBest as any);

                              const expanded = !!expandedTurnIds[t.id];
                              const MAX = 220;

                              return (
                                <Box sx={{ mt: 0.6 }}>
                                  <Box sx={{ whiteSpace: "pre-wrap" }}>
                                    {expanded ? full : truncateText(full, MAX)}
                                  </Box>

                                  {full.length > MAX && (
                                    <Box
                                      sx={{ mt: 0.6, display: "flex", gap: 1 }}
                                    >
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={(e: any) => {
                                          e.stopPropagation();
                                          setExpandedTurnIds((cur) => ({
                                            ...cur,
                                            [t.id]: !expanded,
                                          }));
                                        }}
                                        sx={{
                                          textTransform: "none",
                                          fontWeight: 900,
                                        }}
                                      >
                                        {expanded ? "Less" : "More"}
                                      </Button>
                                    </Box>
                                  )}
                                </Box>
                              );
                            })()}

                          {/* {t.status === "done" && t.responseText && (
        <Box sx={{ mt: 0.4, opacity: 0.8 }}>
          {t.responseText}
        </Box>
      )} */}
                        </Box>

                        <IconButton
                          size="small"
                          onClick={() => deleteTurnById(t.id)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Right: clip + actions */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: 1, display: "flex", alignItems: "center", gap: 1 }}>
            {/* <Button
              size="small"
              variant="outlined"
              disabled={!selected}
              onClick={() => {
                if (!selected) return;
                onOpenRally?.({ packageId: selected.packageId, rallyIndex: selected.rallyIndex });
              }}
              sx={{ textTransform: "none", fontWeight: 900 }}
            >
              Open rally
            </Button> */}

            {/* <Button
              size="small"
              variant="outlined"
              disabled={!selected}
              onClick={() => {
                if (!selected) return;
                const txt = `Q: ${selected.question}\n\nA: ${
                  selected.responseText ?? selected.error ?? ""
                }`;
                navigator.clipboard.writeText(txt).catch(() => {});
              }}
              sx={{ textTransform: "none", fontWeight: 900 }}
            >
              Copy
            </Button> */}

            {/* <Box sx={{ flex: 1 }} /> */}

            {selected && (
              <Box sx={{ fontSize: 12 }}>
                {selected.contextLabel ?? "—"} • Rally {selected.rallyIndex + 1}
              </Box>
            )}
          </Box>

          <Divider />

          <Box
            sx={{
              p: 1,
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {!selected || !clipRow ? (
              <Box sx={{ opacity: 0.8 }}>Select a turn.</Box>
            ) : (
              <>
                <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                  <ActiveRallyVideo
                    insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                    renderPlayerIdentity={renderPlayerIdentity}
                    aiContextLabel={"AI History"}
                    key={`${selected?.id ?? "none"}-${previewNonce}`}
                    focusedFilter={null}
                    packageId={packageId}
                    row={clipRow}
                    rows={rows}
                    activeShotNumber={null}
                    showCondensedTimeline={false}
                    fontColor={"white"}
                    controlsPlacement={"left"}
                    firefightsClips={firefightsClips}
                    firefightRallySet={firefightRallySet}
                    showRateControls={true}
                    showTitle={false}
                    showScoreEtc={false}
                    autoPlayOnRallyChange={true}
                    showAi={true} // IMPORTANT: avoid nested AI inside history viewer
                    aiDrawerMode="split"
                    showLoopScrubber={true}
                    defaultLoopUiOpen={false}
                    showPopoutButton={true}
                    isInFullPopup={false}
                    seedPlayback={{
                      timeSec: clipStartSec(selected),
                      paused: false,
                      nonce: selected.createdAtMs ?? 0,
                    }}
                  />
                </Box>

                <Box sx={{ mt: 1, fontSize: 12, opacity: 0.75 }}>
                  Range: {selected.focus.rangeMode} •{" "}
                  {Math.round(clipStartSec(selected) * 10) / 10}s →{" "}
                  {Math.round(clipEndSec(selected) * 10) / 10}s
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}
